import { downloadSheetAsXlsx, fileNameSlug, sanitizeXmlText } from '@/shared/xlsx';
import {
  moduleStatusFor,
  type StagePhaseStatus,
  type StepCandidate,
  type StepCandidateModule,
} from './stage-details-query';

/**
 * Exportação da lista do drawer "Detalhes" (AA-62 / spec 081): o consultor
 * leva para planilha EXATAMENTE o que está vendo — busca, filtro de estado e
 * nota mínima já aplicados pelo drawer (`rows`). Sem backend: os dados vêm do
 * mesmo payload que alimenta a lista (`/jobs/:id/steps/:stepId/candidates`).
 *
 * Formato alinhado à exportação de respostas de módulo (`responses-matrix`):
 * `ID do usuário · Nome · E-mail …` — o ID permite cruzar as duas planilhas.
 * Na visão de ETAPA, uma coluna por módulo diz em que parte cada candidato
 * está (adendo 04/09, feedback da Déborah Costa — o estado da etapa saía
 * "Em andamento" para todo mundo).
 *
 * Toda string passa por `sanitizeXmlText` (cabeçalho incluso): um único code
 * point proibido em XML corrompe o `.xlsx` inteiro — incidente 2026-07-21 na
 * exportação de respostas. Nome e e-mail vêm de `core.user` (texto digitado).
 */

export interface StageCandidateExportRow {
  candidate: StepCandidate;
  status: StagePhaseStatus;
  score: number | null;
}

/** Rótulos dos badges do drawer — a planilha fala a mesma língua da tela. */
export const STAGE_STATUS_LABEL: Record<StagePhaseStatus, string> = {
  completed: 'Concluído',
  in_progress: 'Em andamento',
  rejected: 'Reprovado',
  pending: 'Pendente',
};

export interface StageCandidatesMatrixOptions {
  /** Visão de ETAPA → colunas do ledger (avançado / reprovado na vaga). Módulo → não. */
  isPhase: boolean;
  /** Etapa/módulo pontuado (algum candidato com nota) → coluna Nota. */
  scored: boolean;
  /**
   * Módulos da etapa (visão de ETAPA): uma coluna por módulo, na ordem do
   * fluxo, com o estado do candidato nele (Concluído / Em andamento /
   * Pendente — régua do drawer de módulo) e, para módulos pontuados, uma
   * coluna "<módulo> · Nota". Ignorado na visão de módulo (a coluna de
   * estado já é a daquele módulo).
   */
  modules?: readonly StepCandidateModule[];
}

const simNao = (v: boolean) => (v ? 'Sim' : 'Não');

/** Rótulos de coluna únicos: oculto ganha sufixo; homônimos ganham (2), (3)… */
function moduleColumnLabels(mods: readonly StepCandidateModule[]): string[] {
  const seen = new Map<string, number>();
  return mods.map((m) => {
    const base = m.hidden ? `${m.name} (oculto)` : m.name;
    const n = (seen.get(base) ?? 0) + 1;
    seen.set(base, n);
    return n === 1 ? base : `${base} (${n})`;
  });
}

/**
 * Matriz da planilha (puro). Colunas: ID do usuário · Nome · Nome social ·
 * E-mail · Estado [· Nota] [· Módulos concluídos n/N · <um módulo>… (+
 * "<módulo> · Nota" quando pontuado)] [· Avançado pelo recrutador · Reprovado
 * na vaga]. Nome civil e
 * nome social em colunas separadas (EU-594: a tela prefere o social; a
 * planilha precisa dos dois). Ordem das linhas = ordem exibida.
 */
export function buildStageCandidatesMatrix(
  rows: readonly StageCandidateExportRow[],
  opts: StageCandidatesMatrixOptions
): { header: string[]; rows: string[][] } {
  // Colunas por módulo (só na visão de etapa), na ordem do fluxo.
  const mods =
    opts.isPhase && opts.modules
      ? [...opts.modules].sort((a, b) => a.position - b.position || a.name.localeCompare(b.name))
      : [];
  const modLabels = moduleColumnLabels(mods);
  // Módulo pontuado = algum candidato exportado tem nota nele.
  const modScored = mods.map((m) =>
    rows.some((r) => moduleStatusFor(r.candidate, m.id).score != null)
  );
  // "Módulos concluídos n/N" — resumo que bate com o funil do rh ("Concluiu
  // {fase}" = todos os módulos visíveis concluídos). O "Estado na etapa" é do
  // ledger e só vira Concluído quando o recrutador passa a pessoa de fase, por
  // isso saía "Em andamento" para quem já tinha terminado tudo (suporte 04/09).
  const visiveis = mods.filter((m) => !m.hidden);
  const resumoModulos = visiveis.length > 0;

  const header = [
    'ID do usuário',
    'Nome',
    'Nome social',
    'E-mail',
    opts.isPhase ? 'Estado na etapa' : 'Estado no módulo',
  ];
  if (opts.scored) header.push('Nota');
  if (resumoModulos) header.push('Módulos concluídos');
  mods.forEach((_, i) => {
    header.push(modLabels[i] as string);
    if (modScored[i]) header.push(`${modLabels[i]} · Nota`);
  });
  if (opts.isPhase) header.push('Avançado pelo recrutador', 'Reprovado na vaga');

  const body = rows.map(({ candidate, status, score }) => {
    const cells = [
      candidate.userId,
      candidate.name ?? '',
      candidate.socialName ?? '',
      candidate.email ?? '',
      STAGE_STATUS_LABEL[status],
    ];
    if (opts.scored) cells.push(score === null ? '' : String(score));
    if (resumoModulos) {
      const n = visiveis.filter(
        (m) => moduleStatusFor(candidate, m.id).status === 'completed'
      ).length;
      cells.push(`${n}/${visiveis.length}`);
    }
    mods.forEach((m, i) => {
      const st = moduleStatusFor(candidate, m.id);
      cells.push(STAGE_STATUS_LABEL[st.status]);
      if (modScored[i]) cells.push(st.score ?? '');
    });
    if (opts.isPhase) {
      cells.push(simNao(candidate.advancedByRecruiter), simNao(candidate.rejected ?? false));
    }
    return cells.map(sanitizeXmlText);
  });

  return { header: header.map(sanitizeXmlText), rows: body };
}

/** `candidatos-<slug do título>[-<estado>]-<AAAA-MM-DD>.xlsx` (data local). */
export function stageCandidatesFileName(
  title: string,
  statusFilter: 'todos' | StagePhaseStatus,
  date: Date = new Date()
): string {
  const base = fileNameSlug(title, 'etapa');
  const estado =
    statusFilter === 'todos'
      ? ''
      : `-${fileNameSlug(STAGE_STATUS_LABEL[statusFilter], statusFilter)}`;
  const dia = [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-');
  return `candidatos-${base}${estado}-${dia}.xlsx`;
}

/** Gera o `.xlsx` e dispara o download (aba "Candidatos"). */
export async function downloadStageCandidatesXlsx(
  rows: readonly StageCandidateExportRow[],
  opts: StageCandidatesMatrixOptions,
  fileName: string
): Promise<void> {
  await downloadSheetAsXlsx(buildStageCandidatesMatrix(rows, opts), {
    fileName,
    sheetName: 'Candidatos',
  });
}
